/*
 * Recording a whole tray slot.
 *
 * The blister is one pod, so the carer records it as one action: everything ticked is given, and
 * anything the resident refuses or the home holds back is unticked and given a reason. A tablet
 * that came out of the blister and was not taken has to go somewhere, so that is asked too.
 */
import { useCallback, useMemo, useState } from 'react';
import { ApiError, api, record } from './api.ts';
import type { Dose, Witness } from './api.ts';
import { useOnline } from './connection.tsx';
import { Overlay } from './overlay.tsx';
import { SealScan } from './scan.tsx';
import { ReportedWarning } from './reported.tsx';

type Choice = {
  given: boolean;
  code: string;
  note: string;
  disposal: 'returned' | 'disposed' | 'left-in-blister';
};

const DISPOSAL: Array<[Choice['disposal'], string]> = [
  ['disposed', 'Disposed of here, following the home\'s policy'],
  ['returned', 'Put aside to go back to the pharmacy'],
  ['left-in-blister', 'Left in the blister, not popped out'],
];

export function SlotCard({
  residentId,
  doses,
  codes,
  witnesses,
  onRecorded,
}: {
  residentId: string;
  doses: Dose[];
  codes: Record<string, string>;
  witnesses: Witness[];
  onRecorded: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanned, setScanned] = useState('');
  const first = doses[0];
  if (!first) return null;

  const separate = doses.every((d) => d.trayType === 'separate');

  return (
    <>
      <div className={`row static column dose ${first.status}`}>
        <div className="row-line">
          <span className="row-main">
            {first.roundName || 'Tray'} {separate ? 'separate tray' : 'tray'}
          </span>
          <span className="time">{first.dueAt}</span>
        </div>
        <p className="muted small">
          {doses.length} item{doses.length === 1 ? '' : 's'} in the blister. Check the seal says{' '}
          <strong>{first.roundName}</strong> and today's date before you pop it.
        </p>

        <ul className="tray-items">
          {doses.map((dose) => (
            <li key={dose.key}>
              {dose.doseText} {dose.name}
              {dose.trayLooks ? <span className="muted small"> — {dose.trayLooks}</span> : null}
              {dose.slotNote ? <span className="chip">{dose.slotNote}</span> : null}
              {dose.reported ? <span className="chip warn">Change reported</span> : null}
            </li>
          ))}
        </ul>

        {doses.filter((dose) => dose.reported).map((dose) => (
          <ReportedWarning key={dose.key} report={dose.reported!} inBlister />
        ))}

        {scanned ? <p className="notice">{scanned}</p> : null}

        <div className="row-actions">
          <button type="button" onClick={() => setOpen(true)}>Record this slot</button>
          <button type="button" className="quiet" onClick={() => setScanning(true)}>Scan the seal</button>
        </div>
      </div>

      {scanning ? (
        <SealScan
          residentId={residentId}
          dueOn={first.dueOn}
          dueAt={first.dueAt}
          roundName={first.roundName || 'tray'}
          onClose={() => setScanning(false)}
          onGood={(text) => setScanned(text)}
        />
      ) : null}

      {open ? (
        <SlotDialog
          residentId={residentId}
          doses={doses}
          codes={codes}
          witnesses={witnesses}
          onClose={() => setOpen(false)}
          onDone={() => {
            setOpen(false);
            onRecorded();
          }}
        />
      ) : null}
    </>
  );
}

function SlotDialog({
  residentId,
  doses,
  codes,
  witnesses,
  onClose,
  onDone,
}: {
  residentId: string;
  doses: Dose[];
  codes: Record<string, string>;
  witnesses: Witness[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [choices, setChoices] = useState<Record<string, Choice>>(() =>
    Object.fromEntries(
      doses.map((dose) => [dose.medicineId, { given: true, code: 'R', note: '', disposal: 'disposed' as const }]),
    ),
  );
  const [witnessId, setWitnessId] = useState('');
  const [earlyReason, setEarlyReason] = useState('');
  const [askEarly, setAskEarly] = useState(doses.some((d) => d.status === 'upcoming'));
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const first = doses[0];
  const notGiven = useMemo(
    () => doses.filter((dose) => !choices[dose.medicineId]?.given),
    [doses, choices],
  );
  const anyControlled = doses.some((dose) => dose.controlledDrug && choices[dose.medicineId]?.given);
  const online = useOnline();
  /*
   * The register balance lives on the server, so a controlled drug in the pod cannot be recorded
   * with no signal. The rest of the pod still can — it is untickable, not unusable.
   */
  const controlledInPod = doses.some((dose) => dose.controlledDrug);
  const blocked = controlledInPod && !online;

  const set = (medicineId: string, patch: Partial<Choice>) =>
    setChoices((current) => ({ ...current, [medicineId]: { ...current[medicineId]!, ...patch } }));

  const submit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setBusy(true);
      setError('');
      try {
        await record(
          '/api/doses/slot',
          {
            residentId,
            dueOn: first?.dueOn,
            dueAt: first?.dueAt,
            earlyReason: earlyReason || undefined,
            items: doses.map((dose) => {
              const choice = choices[dose.medicineId]!;
              // Each tablet in the pod carries its own id, so sending the pod twice records it once.
              const clientId = crypto.randomUUID();
              return choice.given
                ? {
                    clientId,
                    medicineId: dose.medicineId,
                    outcome: 'given',
                    witnessId: dose.controlledDrug ? witnessId || undefined : undefined,
                  }
                : {
                    clientId,
                    medicineId: dose.medicineId,
                    outcome: 'not_given',
                    code: choice.code,
                    note: choice.note,
                    disposal: choice.disposal,
                  };
            }),
          },
          `${doses.length} medicines at ${first?.dueAt ?? ''}`,
        );
        onDone();
      } catch (err) {
        if (err instanceof ApiError && err.status === 409 && /not due until/.test(err.message)) setAskEarly(true);
        setError(err instanceof ApiError ? err.message : 'That did not save.');
      } finally {
        setBusy(false);
      }
    },
    [residentId, first, doses, choices, witnessId, earlyReason, onDone],
  );

  return (
    <Overlay onClose={onClose}>
      <form className="card wide" onSubmit={submit}>
        <h2>{first?.roundName} tray</h2>
        <p className="muted">
          Due {first?.dueAt}. Untick anything that was not taken and say why.
        </p>

        {blocked ? (
          <p className="notice warn">
            <strong>No connection, and there is a controlled drug in this pod.</strong> The register balance is
            kept on the server, so this pod cannot be recorded here until the signal is back. Give it with a
            second person as usual, record it in the paper register, and enter it here when the tablet is
            back online.
          </p>
        ) : !online ? (
          <p className="notice">
            No connection. This will be saved on the tablet and sent when the signal comes back.
          </p>
        ) : null}

        <ul className="list">
          {doses.map((dose) => {
            const choice = choices[dose.medicineId]!;
            return (
              <li key={dose.key}>
                <div className="row static column">
                  <label className="check">
                    <input type="checkbox" checked={choice.given}
                      onChange={(e) => set(dose.medicineId, { given: e.target.checked })} />
                    <span>
                      <strong>{dose.doseText} {dose.name}</strong>
                      {dose.trayLooks ? <span className="muted small"> — {dose.trayLooks}</span> : null}
                      {dose.controlledDrug ? <span className="chip">Controlled drug</span> : null}
                    </span>
                  </label>

                  {!choice.given ? (
                    <div className="slot-extra">
                      <div>
                        <label htmlFor={`code-${dose.medicineId}`}>Why not</label>
                        <select id={`code-${dose.medicineId}`} value={choice.code}
                          onChange={(e) => set(dose.medicineId, { code: e.target.value })}>
                          {Object.entries(codes)
                            .filter(([letter]) => letter !== 'V')
                            .map(([letter, label]) => (
                              <option key={letter} value={letter}>{letter} — {label}</option>
                            ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor={`disposal-${dose.medicineId}`}>What happened to it</label>
                        <select id={`disposal-${dose.medicineId}`} value={choice.disposal}
                          onChange={(e) => set(dose.medicineId, { disposal: e.target.value as Choice['disposal'] })}>
                          {DISPOSAL.map(([id, label]) => (
                            <option key={id} value={id}>{label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor={`note-${dose.medicineId}`}>Note</label>
                        <input id={`note-${dose.medicineId}`} value={choice.note}
                          onChange={(e) => set(dose.medicineId, { note: e.target.value })}
                          placeholder="Spat it out" />
                      </div>
                    </div>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>

        {notGiven.length ? (
          <p className="notice warn">
            {notGiven.length} of {doses.length} not given. Each one is recorded separately on the MAR chart.
          </p>
        ) : null}

        {anyControlled ? (
          <>
            <label htmlFor="slot-witness">Witness, for the controlled drug</label>
            <select id="slot-witness" required value={witnessId} onChange={(e) => setWitnessId(e.target.value)}>
              <option value="">Choose who watched this</option>
              {witnesses.map((w) => (
                <option key={w.id} value={w.id}>{w.name} — {w.jobTitle}</option>
              ))}
            </select>
          </>
        ) : null}

        {askEarly ? (
          <>
            <label htmlFor="slot-early">This slot is not due yet. Why is it being given now?</label>
            <input id="slot-early" value={earlyReason} onChange={(e) => setEarlyReason(e.target.value)}
              placeholder="Going out to an appointment" />
          </>
        ) : null}

        {error ? <p className="error" role="alert">{error}</p> : null}

        <div className="row-actions">
          <button type="submit" disabled={busy || blocked}>
            {busy ? 'Saving…' : notGiven.length ? `Record ${doses.length - notGiven.length} given, ${notGiven.length} not` : 'Record the whole slot as given'}
          </button>
          <button type="button" className="quiet" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </Overlay>
  );
}
